import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RectangleData } from '../../../../core/models/model-viewer/viewer.models';

@Component({
  selector: 'app-model-viewer-2d',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './model-viewer-2d.component.html',
  styleUrls: ['./model-viewer-2d.component.scss'],
})
export class ModelViewer2DComponent implements OnChanges {
  @ViewChild('drawingArea2D') drawingAreaRef!: ElementRef<HTMLDivElement>;

  @Input() modelUrl: string | null = null;
  @Input() rectangles: RectangleData[] = [];
  @Input() isDrawingMode: boolean = false;
  @Input() isSelectMode: boolean = false;
  @Input() pendingRect: RectangleData | null = null;

  @Output() rectangleAdd = new EventEmitter<RectangleData>();
  @Output() rectangleClick = new EventEmitter<number>();
  @Output() modelLoaded = new EventEmitter<void>();

  private isDrawingNow: boolean = false;
  private startPoint: { x: number; y: number } | null = null;
  public currentRect: RectangleData | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modelUrl'] && !changes['modelUrl'].firstChange) {
      this.resetDrawing();
    }
  }

  public onMouseDown(event: MouseEvent): void {
    if (!this.isDrawingMode) return;

    event.preventDefault();
    this.isDrawingNow = true;

    const rect = this.drawingAreaRef.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.startPoint = { x, y };
    this.currentRect = {
      left: x,
      top: y,
      width: 0,
      height: 0,
      note: '',
      id: '',
    };
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDrawingNow || !this.startPoint || !this.drawingAreaRef) return;

    const rect = this.drawingAreaRef.nativeElement.getBoundingClientRect();
    const currentX = event.clientX - rect.left;
    const currentY = event.clientY - rect.top;

    const left = Math.min(this.startPoint.x, currentX);
    const top = Math.min(this.startPoint.y, currentY);
    const width = Math.abs(currentX - this.startPoint.x);
    const height = Math.abs(currentY - this.startPoint.y);

    this.currentRect = { left, top, width, height, note: '', id: '' };
  }

  @HostListener('window:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (!this.isDrawingNow) return;

    this.isDrawingNow = false;

    if (
      this.currentRect &&
      this.currentRect.width > 5 &&
      this.currentRect.height > 5
    ) {
      this.rectangleAdd.emit(this.currentRect);
    }

    this.resetDrawing();
  }

  public onRectangleClick(event: MouseEvent, index: number): void {
    event.stopPropagation();

    if (!this.isSelectMode) return;

    this.rectangleClick.emit(index);
  }

  private resetDrawing(): void {
    this.isDrawingNow = false;
    this.currentRect = null;
    this.startPoint = null;
  }

  public onTouchStart(event: TouchEvent): void {
    if (!this.isDrawingMode) return;
    if (event.touches.length !== 1) return;

    event.preventDefault();
    this.isDrawingNow = true;

    const touch = event.touches[0];
    const coords = this.getTouchCoordinates(touch);

    this.startPoint = coords;
    this.currentRect = {
      left: coords.x,
      top: coords.y,
      width: 0,
      height: 0,
      note: '',
      id: '',
    };
  }

  @HostListener('window:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.isDrawingNow || !this.startPoint || !this.drawingAreaRef) return;
    if (event.touches.length !== 1) return;

    event.preventDefault();

    const touch = event.touches[0];
    const coords = this.getTouchCoordinates(touch);

    const left = Math.min(this.startPoint.x, coords.x);
    const top = Math.min(this.startPoint.y, coords.y);
    const width = Math.abs(coords.x - this.startPoint.x);
    const height = Math.abs(coords.y - this.startPoint.y);

    this.currentRect = { left, top, width, height, note: '', id: '' };
  }

  @HostListener('window:touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.isDrawingNow) return;

    event.preventDefault();
    this.isDrawingNow = false;

    if (
      this.currentRect &&
      this.currentRect.width > 5 &&
      this.currentRect.height > 5
    ) {
      this.rectangleAdd.emit(this.currentRect);
    }

    this.resetDrawing();
  }

  public onRectangleTouchStart(event: TouchEvent, index: number): void {
    event.stopPropagation();
    event.preventDefault();

    if (!this.isSelectMode) return;

    this.rectangleClick.emit(index);
  }

  private getTouchCoordinates(touch: Touch): { x: number; y: number } {
    if (!this.drawingAreaRef) return { x: 0, y: 0 };

    const rect = this.drawingAreaRef.nativeElement.getBoundingClientRect();
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  }
}
